import { FC, useState } from "react";
import { AppThemeData } from "../Types";
import { Box, Paper, Tab, Tabs, Typography } from "@material-ui/core";
import ColorPropertiesList from "../Tabs/ColorPropertiesList";
import { getFilteredFontProperties, getFilteredIconProperties, getPropertiesByCategory } from "../Helpers";
import IconPropertiesList from "../Tabs/IconPropertiesList";
import FontPropertiesList from "../Tabs/FontPropertiesList";
import NumericPropertiesList from "../Tabs/NumericPropertiesList";
import OptionsPropertiesList from "../Tabs/OptionsPropertiesList";
import NoDataIndicator from "src/components/ui-components/NoDataAvailableIndicator";
import ChartPropertiesList from "../Tabs/ChartPropertiesList";
import BehaviorPropertiesList from "../Tabs/BehaviorPropertiesList";

type PropertiesCardProps = {
  propertiesList: AppThemeData;
  isDarkFilter: boolean;
};

type TabType = "Color" | "Fonts" | "Icons" | "Spacing" | "Dimension" | "Other" | "Behavior" | "Chart";

const PropertyPane: FC<PropertiesCardProps> = ({ propertiesList, isDarkFilter }) => {
  const [activeTab, setActiveTab] = useState<number>(0);

  const tabs: TabType[] = ["Color", "Fonts", "Icons", "Chart", "Spacing", "Dimension", "Behavior", "Other"];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const renderTabContent = () => {
    const currentTab = tabs[activeTab];

    const renderOrFallback = (condition: boolean, component: JSX.Element) =>
      condition ? component : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <NoDataIndicator />
        </Box>
      );

    switch (currentTab) {
      case "Color": {
        const colorProperties = getPropertiesByCategory(propertiesList.genericThemeProperties, 'color');
        return renderOrFallback(
          colorProperties.length > 0,
          <ColorPropertiesList propertiesList={colorProperties} isDarkFilter={isDarkFilter} />
        );
      }

      case "Fonts": {
        const fontProperties = getFilteredFontProperties(propertiesList.fontProperties, isDarkFilter);
        return renderOrFallback(
          fontProperties.length > 0,
          <FontPropertiesList propertiesList={fontProperties} />
        );
      }

      case "Icons": {
        const iconProperties = getFilteredIconProperties(propertiesList.iconProperties, isDarkFilter);
        return renderOrFallback(
          iconProperties.length > 0,
          <IconPropertiesList propertiesList={iconProperties} />
        );
      }

      case "Chart": {
        const chartProperties = getPropertiesByCategory(propertiesList.genericThemeProperties, 'chart');
        return renderOrFallback(
          chartProperties.length > 0,
          <ChartPropertiesList propertiesList={chartProperties} isDarkFilter={isDarkFilter} />
        );
      }

      case "Spacing": {
        const spacingProperties = getPropertiesByCategory(propertiesList.genericThemeProperties, 'spacing');
        return renderOrFallback(
          spacingProperties.length > 0,
          <NumericPropertiesList propertiesList={spacingProperties} />
        );
      }

      case "Dimension": {
        const dimensionProperties = getPropertiesByCategory(propertiesList.genericThemeProperties, 'dimension');
        return renderOrFallback(
          dimensionProperties.length > 0,
          <NumericPropertiesList propertiesList={dimensionProperties} />
        );
      }

      case "Behavior": {
        const behaviorProperties = getPropertiesByCategory(propertiesList.genericThemeProperties, 'behavior');
        return renderOrFallback(
          behaviorProperties.length > 0,
          <BehaviorPropertiesList propertiesList={behaviorProperties} />
        );
      }

      case "Other": {
        const customProperties = getPropertiesByCategory(propertiesList.genericThemeProperties, 'custom');
        return renderOrFallback(
          customProperties.length > 0,
          <OptionsPropertiesList propertiesList={customProperties} type="custom" />
        );
      }

      default:
        return (
          <Box p={3}>
            <Typography>Select a tab to view properties</Typography>
          </Box>
        );
    }
  };

  if (!propertiesList) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No properties data available</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ width: '100%', maxWidth: '100%', mx: 'auto', height: '85%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minWidth: 'auto',
              px: 2,
              py: 1.5,
              fontSize: '0.875rem',
              fontWeight: 500,
            }
          }}
        >
          {tabs.map((tab) => (
            <Tab
              sx={{
                '&.Mui-selected': {
                  backgroundColor: '#072f40',
                  color: '#ffffff !important'
                },
              }}
              key={tab}
              label={tab}
            />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ height: '100%' }}>
        {renderTabContent()}
      </Box>
    </Paper>
  );
};

export default PropertyPane;